/**
 * DocumentsPanel — generic document storage UI for any CRM entity.
 * First surface: the Documents tab in ContactDetailDrawer (recruiting
 * resumes on candidate contacts).
 *
 *   <DocumentsPanel
 *     entityType="contact"
 *     entityId={contact.id}
 *     active={selectedTab === DOCUMENTS_TAB_INDEX}   // lazy first fetch
 *     defaultCategory="resume"                        // preselected on upload
 *   />
 *
 * All authorization happens server-side in the manageCrmDocuments callable
 * (Firebase auth → CRM role → live entity ownership). This panel just renders
 * whatever the backend allows and surfaces permission errors gracefully.
 */

import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  IconButton,
  Button,
  Badge,
  Select,
  Spinner,
  Center,
  Tooltip,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiFolder,
  FiUploadCloud,
  FiMoreVertical,
  FiDownload,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiLock,
} from 'react-icons/fi';
import { useCrmDocuments } from '../../../hooks/useCrmDocuments';
import {
  DOCUMENT_CATEGORIES,
  FILE_INPUT_ACCEPT,
  getCategoryMeta,
  getFileKind,
  formatFileSize,
} from '../../../constants/documentConstants';
import { getFileKindStyle } from './fileKindStyles';
import DocumentViewerModal from './DocumentViewerModal';

function formatUploadedAt(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DocumentsPanel({
  entityType,
  entityId,
  active = true,
  defaultCategory = 'general',
}) {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const cancelDeleteRef = useRef(null);
  const renameInputRef = useRef(null);

  const {
    documents,
    loading,
    error,
    accessDenied,
    uploadingFiles,
    busyDocIds,
    refresh,
    uploadFiles,
    getUrl,
    rename,
    remove,
  } = useCrmDocuments(entityType, entityId, { enabled: active });

  const [isDragging, setIsDragging] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(defaultCategory);
  const [renameTarget, setRenameTarget] = useState(null); // document being renamed
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // document pending delete confirm
  const [deleting, setDeleting] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null); // null = viewer closed

  const renameModal = useDisclosure();

  const dropBg = useColorModeValue('gray.50', 'gray.700');
  const dropActiveBg = useColorModeValue('blue.50', 'blue.900');
  const dropBorder = useColorModeValue('gray.300', 'gray.600');
  const dropActiveBorder = useColorModeValue('blue.400', 'blue.300');
  const rowBorder = useColorModeValue('gray.200', 'gray.600');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const thumbFallbackBg = useColorModeValue('gray.100', 'gray.600');

  // New entity (or a candidate vs regular contact) → reset the category picker.
  useEffect(() => {
    setUploadCategory(defaultCategory);
  }, [entityId, defaultCategory]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const { results } = await uploadFiles(files, { category: uploadCategory });
    const succeeded = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);
    if (succeeded.length > 0) {
      toast({
        title: succeeded.length === 1 ? 'Document uploaded' : `${succeeded.length} documents uploaded`,
        status: 'success',
        duration: 3000,
      });
    }
    failed.forEach((r) => {
      toast({
        title: `Couldn\u2019t upload ${r.fileName}`,
        description: r.error,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    });
  };

  const handleFileInputChange = (e) => {
    handleUpload(e.target.files);
    e.target.value = ''; // allow re-selecting the same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer?.files);
  };

  /** Open the in-page viewer at this document (new-tab View lives in its header). */
  const openViewer = (doc) => {
    const idx = documents.findIndex((d) => d.id === doc.id);
    if (idx >= 0) setViewerIndex(idx);
  };

  const handleDownload = async (doc) => {
    const res = await getUrl(doc.id, 'download');
    if (res.success && res.url) {
      // Signed URL is served with content-disposition: attachment — an
      // anchor click downloads without navigating away from the app.
      const a = document.createElement('a');
      a.href = res.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      toast({ title: 'Download failed', description: res.error, status: 'error', duration: 5000 });
    }
  };

  const openRename = (doc) => {
    setRenameTarget(doc);
    setRenameValue(doc.fileName);
    renameModal.onOpen();
  };

  const handleRenameSave = async () => {
    if (!renameTarget) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === renameTarget.fileName) {
      renameModal.onClose();
      return;
    }
    setRenameSaving(true);
    const res = await rename(renameTarget.id, trimmed);
    setRenameSaving(false);
    if (res.success) {
      renameModal.onClose();
    } else {
      toast({ title: 'Rename failed', description: res.error, status: 'error', duration: 5000 });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const res = await remove(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (res.success) {
      toast({ title: 'Document deleted', status: 'success', duration: 2500 });
    } else {
      toast({ title: 'Delete failed', description: res.error, status: 'error', duration: 5000 });
    }
  };

  // --------------------------------------------------------------------- UI

  if (accessDenied) {
    return (
      <Center py={12}>
        <VStack spacing={3}>
          <Icon as={FiLock} boxSize={8} color="gray.400" />
          <Text fontWeight="medium">No access to these documents</Text>
          <Text fontSize="sm" color={mutedColor} textAlign="center" maxW="sm">
            Documents are only visible to the record owner and CRM admins.
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      <HStack justify="space-between">
        <HStack spacing={2}>
          <Heading size="sm">Documents</Heading>
          {documents.length > 0 && (
            <Badge borderRadius="full" px={2} colorScheme="blue">
              {documents.length}
            </Badge>
          )}
        </HStack>
        <Tooltip label="Refresh">
          <IconButton
            aria-label="Refresh documents"
            icon={<FiRefreshCw />}
            size="sm"
            variant="ghost"
            isLoading={loading}
            onClick={refresh}
          />
        </Tooltip>
      </HStack>

      {/* Upload zone */}
      <Box
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={isDragging ? dropActiveBorder : dropBorder}
        borderRadius="lg"
        bg={isDragging ? dropActiveBg : dropBg}
        px={4}
        py={6}
        textAlign="center"
        transition="background 0.15s, border-color 0.15s"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <VStack spacing={3}>
          <Icon as={FiUploadCloud} boxSize={7} color={isDragging ? 'blue.400' : 'gray.400'} />
          <Text fontSize="sm" color={mutedColor}>
            Drag &amp; drop files here, or
          </Text>
          <HStack spacing={3}>
            <Select
              size="sm"
              width="auto"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              aria-label="Document category"
            >
              {DOCUMENT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<FiUploadCloud />}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse Files
            </Button>
          </HStack>
          <Text fontSize="xs" color="gray.500">
            PDF, Word, Excel, PowerPoint, text, and images &middot; up to 25 MB each
          </Text>
        </VStack>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={FILE_INPUT_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </Box>

      {/* In-flight uploads */}
      {uploadingFiles.map((f) => (
        <HStack
          key={f.key}
          borderWidth="1px"
          borderColor={rowBorder}
          borderRadius="md"
          px={3}
          py={2}
          spacing={3}
        >
          <Spinner size="sm" color="blue.400" />
          <Text fontSize="sm" noOfLines={1} flex={1}>
            {f.name}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {formatFileSize(f.size)}
          </Text>
        </HStack>
      ))}

      {/* Load error */}
      {error && !loading && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box flex={1}>{error}</Box>
          <Button size="xs" variant="outline" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Document list */}
      {loading && documents.length === 0 ? (
        <Center py={8}>
          <Spinner color="blue.400" />
        </Center>
      ) : documents.length === 0 && !error ? (
        <Center py={8}>
          <VStack spacing={2}>
            <Icon as={FiFolder} boxSize={8} color="gray.400" />
            <Text fontWeight="medium">No documents yet</Text>
            <Text fontSize="sm" color={mutedColor}>
              Upload resumes, agreements, licenses, and more.
            </Text>
          </VStack>
        </Center>
      ) : (
        <VStack align="stretch" spacing={2}>
          {documents.map((doc) => {
            const kindStyle = getFileKindStyle(getFileKind(doc.mimeType, doc.fileName));
            const category = getCategoryMeta(doc.category);
            const isBusy = busyDocIds.includes(doc.id);
            const iconTile = (
              <Center boxSize="48px" borderRadius="md" bg={thumbFallbackBg}>
                <Icon as={kindStyle.icon} boxSize={5} color={kindStyle.color} />
              </Center>
            );
            return (
              <HStack
                key={doc.id}
                borderWidth="1px"
                borderColor={rowBorder}
                borderRadius="md"
                px={3}
                py={2.5}
                spacing={3}
                _hover={{ bg: rowHoverBg }}
                align="center"
              >
                {/* Thumbnail (or type icon) — click to open the viewer */}
                <Box
                  flexShrink={0}
                  cursor="pointer"
                  onClick={() => openViewer(doc)}
                  borderRadius="md"
                  overflow="hidden"
                  borderWidth="1px"
                  borderColor={rowBorder}
                >
                  {doc.thumbnailUrl ? (
                    <Image
                      src={doc.thumbnailUrl}
                      alt=""
                      boxSize="48px"
                      objectFit="cover"
                      fallback={iconTile}
                    />
                  ) : (
                    iconTile
                  )}
                </Box>
                <Box flex={1} minW={0}>
                  <Tooltip label={doc.fileName} openDelay={500}>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      noOfLines={1}
                      cursor="pointer"
                      _hover={{ textDecoration: 'underline', color: 'blue.400' }}
                      onClick={() => openViewer(doc)}
                    >
                      {doc.fileName}
                    </Text>
                  </Tooltip>
                  <HStack spacing={2} mt={0.5}>
                    <Badge colorScheme={category.color} fontSize="0.65rem">
                      {category.label}
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(doc.fileSize)}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {formatUploadedAt(doc.createdAt)}
                      {doc.uploadedByName ? ` \u00b7 ${doc.uploadedByName}` : ''}
                    </Text>
                  </HStack>
                </Box>
                <Menu placement="bottom-end">
                  <MenuButton
                    as={IconButton}
                    aria-label="Document actions"
                    icon={isBusy ? <Spinner size="xs" /> : <FiMoreVertical />}
                    size="sm"
                    variant="ghost"
                    isDisabled={isBusy}
                  />
                  <MenuList fontSize="sm">
                    <MenuItem icon={<FiEye />} onClick={() => openViewer(doc)}>
                      Preview
                    </MenuItem>
                    <MenuItem icon={<FiDownload />} onClick={() => handleDownload(doc)}>
                      Download
                    </MenuItem>
                    <MenuItem icon={<FiEdit2 />} onClick={() => openRename(doc)}>
                      Rename
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => setDeleteTarget(doc)}>
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            );
          })}
        </VStack>
      )}

      {/* In-page document viewer */}
      <DocumentViewerModal
        isOpen={viewerIndex !== null}
        onClose={() => setViewerIndex(null)}
        documents={documents}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        getUrl={getUrl}
      />

      {/* Rename modal */}
      <Modal
        isOpen={renameModal.isOpen}
        onClose={renameModal.onClose}
        size="md"
        initialFocusRef={renameInputRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Rename document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSave();
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={renameModal.onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleRenameSave} isLoading={renameSaving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={!!deleteTarget}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setDeleteTarget(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md">Delete document</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Delete <strong>{deleteTarget?.fileName}</strong>? The file will be permanently
              removed from storage. This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} variant="ghost" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deleting} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
}
